<template>
  <div class="space-y-1" qaid="store-pickup-fulfillment-block">
    <div class="flex gap-x-1 items-center">
      <pick-up-in-store-icon />
      <span qaid="store-pickup-header" class="text-md font-bold">{{ $t(option.name) }}</span>
    </div>
    <div v-if="hasMessaging" class="leading-normal text-sm">
      <i18n-t v-if="isOutOfStockMessage || baseMessage === soldOutMessage" :keypath="baseMessage" tag="p">
        <template #retailer>
          <pdl-button small underline text qaid="select-retailer-button" @click="setIsDrawerOpen(true)">
            {{ $t(nearbyRetailersMessage) }}
          </pdl-button>
        </template>
        <template #guidance>
          <pdl-link class="hover:no-underline" href="/contactUs/">{{ $t(guidanceMessage) }}</pdl-link>
        </template>
      </i18n-t>
      <i18n-t v-else-if="baseMessage && baseMessage === notAvailableMessage" :keypath="baseMessage" tag="p">
        <template #retailer>
          <pdl-button small underline text qaid="select-retailer-button" @click="setIsDrawerOpen(true)">
            {{ $t(viewOtherStoresMessage) }}
          </pdl-button>
        </template>
      </i18n-t>
      <i18n-t
        v-else-if="baseMessage && isStorePickupSoonMessage"
        qaid="store-pickup-soon-content"
        :keypath="baseMessage"
        tag="p"
      >
        <template #retailer>
          <pdl-button small underline text qaid="select-retailer-button" @click="setIsDrawerOpen(true)">
            {{ selectedRetailer.displayName }}
          </pdl-button>
        </template>
      </i18n-t>
      <i18n-t v-else-if="baseMessage" qaid="store-pickup-content" class="text-pretty" :keypath="baseMessage" tag="p">
        <template #retailer>
          <pdl-button small underline text qaid="select-retailer-button" @click="setIsDrawerOpen(true)">
            {{ selectedRetailer.displayName }}
          </pdl-button>
        </template>
        <template #startDate>
          <span class="font-bold">{{ startDate }}</span>
        </template>
        <template #endDate>
          <span class="font-bold">{{ endDate }}</span>
        </template>
      </i18n-t>
      <div v-if="isBopis && hasLocallyPrice" class="flex gap-x-1 items-center text-xs rounded-sm mt-25">
        <span v-if="isComparableValueVisible" class="line-through">{{ displayPrice }}</span>
        <pdl-button
          tertiary
          :icon="'info_outline'"
          icon-right
          xsmall
          class="retailer-pricing-text"
          @click="setIsPricingDrawerOpen(true)"
        >
          {{ retailerPrice }}
        </pdl-button>
      </div>
      <p v-if="buildTimeMessage" class="mt-1" qaid="store-pickup-locally-message">{{ $t(buildTimeMessage) }}</p>
      <div v-if="dealerDeliveryMessage && !hasRichMessaging" class="flex gap-x-2">{{ $t(dealerDeliveryMessage) }}</div>
      <p v-if="locallyMessage" qaid="store-pickup-locally-message">{{ $t(locallyMessage) }}</p>
      <div v-if="retailersNearYou" class="flex gap-x-1 mt-2">
        <div class="text-green-140">
          <pdl-icon name="check_circle" size="18" />
          <span qaid="store-pickup-near-stores-message">
            {{ $t('fulfillment.storePickup.nearStore.message', [retailersNearYou]) }}
          </span>
        </div>
        <pdl-button small underline text qaid="store-pickup-find-stores-button" @click="setIsDrawerOpen(true)">
          {{ $t('pickup.search.button') }}
        </pdl-button>
      </div>
    </div>
    <pdl-button v-else small underline text qaid="select-retailer-button" @click="setIsDrawerOpen(true)">
      {{ $t('checkout.multi.retailer.error') }}
    </pdl-button>
  </div>
</template>

<script>
import {PdlIcon} from '@pedal/pdl-icon';
import {PdlButton} from '@pedal/pdl-button';
import {PdlLink} from '@pedal/pdl-link';
import PickUpInStoreIcon from '@/components/containers/buy-zone/b2c/fulfillment-options/store-pick-up/PickUpInStoreIcon.vue';
import {mapGetters, mapState} from 'vuex';
import {useFulfillmentOptionMessaging} from '@/composables/fulfillment-option-messaging.js';
import {toRefs} from 'vue';
import {DeliveryModeTypes} from '@/constants/delivery-modes';

const NEARBY_RETAILERS_BP_KEY = 'fulfillment.storePickup.selectedRetailer.retailerNearYou';
const NOT_AVAILABLE_BP_KEY = 'fulfillment.storePickup.selectedRetailer.notAvailable';
const SOLD_OUT_BP_KEY = 'fulfillment.storePickup.selectedRetailer.soldOut';
const OUT_OF_STOCK_BP_KEY = 'fulfillment.storePickup.selectedRetailer.outOfStock';
const VIEW_STORES_BP_KEY = 'fulfillment.storePickup.selectedRetailer.viewOtherStores';
const STORE_PICKUP_TODAY_BP_KEY = 'fulfillment.storePickup.availability.today.atRetailer';
const STORE_PICKUP_48HRS_BP_KEY = 'fulfillment.storePickup.availability.48hrs.atRetailer';
const GUIDANCE_BP_KEY = 'fulfillment.storePickup.selectedRetailer.guidance';

export default {
  components: {PdlButton, PdlIcon, PdlLink, PickUpInStoreIcon},
  props: {
    setIsDrawerOpen: {
      type: Function,
      default: () => {},
    },
    setIsPricingDrawerOpen: {
      type: Function,
      default: () => {},
    },
    option: {
      type: Object,
      default: () => ({}),
    },
    variantCode: {
      type: String,
      default: '',
      required: true,
    },
  },
  setup(props) {
    const {option} = toRefs(props);
    const {
      hasRichMessaging,
      hasMessaging,
      startDate,
      endDate,
      locallyPrice,
      hasLocallyPrice,
      baseMessage,
      locallyMessage,
      buildTimeMessage,
      dealerDeliveryMessage,
    } = useFulfillmentOptionMessaging(option);

    return {
      hasRichMessaging,
      hasMessaging,
      startDate,
      endDate,
      hasLocallyPrice,
      locallyPrice,
      baseMessage,
      locallyMessage,
      buildTimeMessage,
      dealerDeliveryMessage,
    };
  },
  data() {
    return {
      notAvailableMessage: NOT_AVAILABLE_BP_KEY,
      soldOutMessage: SOLD_OUT_BP_KEY,
      viewOtherStoresMessage: VIEW_STORES_BP_KEY,
      guidanceMessage: GUIDANCE_BP_KEY,
      outOfStockMessage: OUT_OF_STOCK_BP_KEY,
      nearbyRetailersMessage: NEARBY_RETAILERS_BP_KEY,
    };
  },
  computed: {
    ...mapState('user', ['selectedRetailer']),
    ...mapState('productDetails', ['isLocallyActive', 'locallyStockAvailableRetailers']),
    ...mapGetters('productDetails', ['getConsumerPriceForVariant']),

    isStorePickupSoonMessage() {
      return this.baseMessage === STORE_PICKUP_TODAY_BP_KEY || this.baseMessage === STORE_PICKUP_48HRS_BP_KEY;
    },
    isOutOfStockMessage() {
      return this.baseMessage && this.baseMessage === this.outOfStockMessage;
    },
    retailersNearYou() {
      return this.locallyStockAvailableRetailers.some((retailer) => retailer.name === this.selectedRetailer.name)
        ? this.locallyStockAvailableRetailers.length - 1
        : this.locallyStockAvailableRetailers.length;
    },
    displayPrice() {
      return this.getConsumerPriceForVariant(this.variantCode).displayPriceValue.value;
    },
    retailerPrice() {
      return this.$t('text.buyingzone.retailerPrice.price', [this.locallyPrice]);
    },
    isBopis() {
      return (
        this.option.deliveryModes?.some((fo) => fo.code.includes(DeliveryModeTypes.BOPIS)) &&
        !Object.keys(this.option?.deliveryWindow)?.length
      );
    },
    isComparableValueVisible() {
      return this.displayPrice !== this.locallyPrice;
    },
  },
};
</script>

<style lang="scss" scoped>
.retailer-pricing-text {
  color: var(--blue-100);
  background-color: var(--blue-10);
  padding: 0.125rem 0.375rem;

  &:hover {
    color: var(--blue-100);
    background-color: var(--blue-10);
  }
}
</style>
